import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { meApi } from '../user/meApi';
import { transactionApi } from '../transaction/transactionApi';
import { profileApi } from '../profile/profileApi';
import { tradeApi } from '../trade/tradeApi';

export interface TransferPointsRequest {
  recipientId: number;
  amount: number;
  message: string;
  tradeId?: string;
}

export interface TransferPointsResponse {
  success: boolean;
  message?: string;
  senderNewBalance: number;
  amount: number;
}

export interface TransferLimitsResponse {
  isElite: boolean;
  dailyCount: number;
  dailyPoints: number;
  transfersRemaining: number;
  pointsRemaining: number;
}

export interface TransferLimitError {
  code: 'DAILY_TRANSFER_LIMIT' | 'DAILY_POINTS_LIMIT' | 'RECIPIENT_ACCOUNT_AGE';
  message: string;
  currentLimit?: number;
  eliteLimit?: number;
  remaining?: number;
  upgradeUrl?: string;
}

export const pointsApi = createApi({
  reducerPath: 'pointsApi',
  baseQuery: baseQuery,
  tagTypes: ['PointTransfers', 'TransferLimits'],
  endpoints: (builder) => ({
    getTransferLimits: builder.query<TransferLimitsResponse, void>({
      query: () => ({
        url: '/points/transfer-limits',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['TransferLimits'],
    }),
    transferPoints: builder.mutation<TransferPointsResponse, TransferPointsRequest>({
      query: (body) => ({
        url: '/points/transfer',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TransferLimits'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(meApi.util.invalidateTags(['User']));
          dispatch(transactionApi.util.invalidateTags(['Transactions']));
          dispatch(profileApi.util.invalidateTags(['PublicProfile']));
          // Send packages carry each receiver's balance; the Finalize Trade modal
          // reads it, so a transfer made from there must show without closing it.
          dispatch(tradeApi.util.invalidateTags(['TradePackages']));
        } catch {
          // Mutation itself handles error states
        }
      },
    }),
  }),
});

export const { useGetTransferLimitsQuery, useTransferPointsMutation } = pointsApi;
